import { MessageRenderer } from "./messageRenderer/MessageWithTableRenderer";
import {
  FaqAnswerRenderer,
  FaqStageIndicator,
} from "./messageRenderer/FaqAnswerRenderer";
import VideoCards, { stripVideoList } from "./messageRenderer/VideoCards";
import SpeakButton from "./tts/SpeakButton";
import { isSpeakable } from "./tts/speechText";
import Spinner from "./spinner";

// A reply is worth offering aloud once it has real text — not while it's still
// a "Thinking..." placeholder, a FAQ stage label, or mid-stream. isSpeakable
// also leaves the button off a reply its voice has nothing to say for, rather
// than offering one that could only fail.
const canSpeak = (message) =>
  message.type !== "user" &&
  Boolean(message.text?.trim()) &&
  message.text !== "Thinking..." &&
  !message.faqStageOnly &&
  !message.streaming &&
  isSpeakable(message);

const MessagesArea = ({
  messages,
  avatarThumb,
  starterPrompts,
  stylePreset,
  primaryColor,
  radius,
  isLoadingChat,
  messagesEndRef,
  onSendMessage,
  isExpanded,
  language,
  // false while "AI video Q&A" is selected — no speaker buttons then
  speechEnabled = true,
}) => (
  <div
    className={`flex flex-col overflow-y-auto messages-scrollbar py-4 px-2 ${!isExpanded ? "w-[95%]" : "w-[100%]"} h-full space-y-3 bg-gray-50 rounded-md relative`}
  >
    {/* Loading overlay when switching sessions */}
    {isLoadingChat && (
      <div className="absolute inset-0 bg-gray-50/80 flex items-center justify-center z-10 rounded-md">
        <div className="text-center">
          <div className="flex justify-center mb-3">
            <Spinner size="h-12 w-12" />
          </div>
          <p className="text-sm text-gray-600">Loading chat...</p>
        </div>
      </div>
    )}

    {messages.map((message, index) => (
      <div
        key={message.id || index}
        className={`flex items-start gap-2 ${message.type === "user" ? "justify-end" : "justify-start"}`}
      >
        {/* {message.type !== "user" && (
          <img
            src={avatarThumb}
            alt="AI"
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        )} */}
        <div
          className={`px-2 py-2  ${
            message.type === "user"
              ? "text-black max-w-[75%] px-3 py-2 text-left rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl"
              : "text-gray-800 max-w-[100%]"
          }`}
          style={{
            backgroundColor: message.type === "user" ? `${primaryColor}33` : "",
            border:
              message.type === "user" ? `1px solid ${primaryColor}44` : ``,
          }}
        >
          {/* Animated thinking dots while waiting for bot response */}
          {message.text === "Thinking..." ? (
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-600">Thinking</span>
              <div className="flex space-x-1 ml-1">
                {[0, 0.2, 0.4].map((delay, i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"
                    style={{ animationDelay: `${delay}s` }}
                  />
                ))}
              </div>
            </div>
          ) : message.isFaqAgent && message.faqStageOnly ? (
            <FaqStageIndicator label={message.text} />
          ) : (
            <div
              className={`${isExpanded ? "text-[18px]" : message.type === "user" ? "text-[14px]" : "text-[12px]"}`}
            >
              {message.isFaqAgent ? (
                <FaqAnswerRenderer
                  text={message.text}
                  source={message.source}
                  webSources={message.webSources}
                  matchedFaqs={message.matchedFaqs}
                />
              ) : (
                <MessageRenderer
                  // The video cards below carry the videos the text lists.
                  text={stripVideoList(message)}
                  primaryColor={primaryColor}
                />
              )}
              <VideoCards
                videos={message.videos}
                language={language?.startsWith("zh") ? "zh" : "en"}
              />
            </div>
          )}
          {/* A later block may still be on its way — the video path, finding
              no video, first sends "Let me answer in text instead" and only
              then the text answer — so a reply with some text in it keeps the
              dots running until the answer has finished. */}
          {message.streaming && !message.faqStageOnly && (
            <div className="mt-2">
              <FaqStageIndicator
                label={
                  language?.startsWith("zh")
                    ? "正在整理答案..."
                    : "Composing the answer..."
                }
              />
            </div>
          )}
          <div
            className={`flex items-center gap-2 ${message.type === "user" ? "justify-end" : "justify-start"}`}
          >
            <p
              className={`text-[8px] ${message.type === "user" ? "text-black/50" : "text-gray-500"}`}
            >
              {message.timestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            {speechEnabled && canSpeak(message) && (
              <SpeakButton
                id={message.id || `msg-${index}`}
                message={message}
                primaryColor={primaryColor}
              />
            )}
          </div>
        </div>
      </div>
    ))}

    {/* Starter prompts — shown only before first user message */}
    {starterPrompts.filter(Boolean).length > 0 && messages.length <= 1 && (
      <div className="flex flex-col gap-1.5 pt-1 pb-2">
        <div>
          <p className="text-sm text-gray-600">Use below starter prompts:</p>
        </div>
        {starterPrompts.filter(Boolean).map((prompt, i) => (
          <button
            key={i}
            onClick={() => onSendMessage(prompt)}
            className="px-3 py-1.5 text-[13px] leading-snug bg-white cursor-pointer"
            style={{
              borderRadius: stylePreset === "sharp" ? 0 : 20,
              border: `1px solid ${primaryColor}`,
              color: primaryColor,
            }}
          >
            {prompt}
          </button>
        ))}
      </div>
    )}
    <div ref={messagesEndRef} />

    <style>{`
      .messages-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
      }
      .messages-scrollbar::-webkit-scrollbar {
        width: 5px;
      }
      .messages-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .messages-scrollbar::-webkit-scrollbar-thumb {
        background-color: rgba(148, 163, 184, 0.5);
        border-radius: 9999px;
      }
      .messages-scrollbar:hover::-webkit-scrollbar-thumb {
        background-color: rgba(100, 116, 139, 0.65);
      }
    `}</style>
  </div>
);

export default MessagesArea;
